import { Component, OnDestroy } from '@angular/core'
import {
  SummaryDataTableComponent,
  SummaryDataTableData,
  SummaryDataTableOptions
} from '../../common/summary-data-table/summary-data-table.component'
import { ActivityAPIModel, PaginatedAPI, UserAPIModel, activityType, activityTypes } from 'src/app/models/api.model'
import { Observable, Subscription, of } from 'rxjs'
import { AppState } from 'src/app/store/state/app.state'
import { DocumentService } from 'src/app/services/common/document.service'
import { Store } from '@ngrx/store'
import {
  activitySummaryDetailsSelector,
  getActivityTypes,
  isPendingActivitesUpdated
} from 'src/app/store/selector/activity.selector'
import { ServiceUrlConstants } from 'src/app/constants/service-url-constants'
import {
  COMPANY_ENTITY_DISPLAY_ID,
  ACTIVITY_NAME,
  REQUEST_INITIATED_ON,
  DAYS_ELAPSED,
  RESPONSE_DUE_BY,
  TASK_CURRENT_STATUS,
  ASSIGNED_TO_DISPLAY_ID,
  CREATED,
  PENDING_ACTIVITY_ID
} from 'src/app/constants/ui.columns'
import {
  LOAD_ACTIVITY_SUMMARY_DETAILS,
  fetch_activity_types_data,
  update_activity_pending_item_success
} from 'src/app/store/action/activity.action'
import { ActivityService } from 'src/app/services/activity/activity.service'
import { GlobalConstants } from 'src/app/constants/global-constants'
import { getUIStatusMappingFromState, getUserFromState } from 'src/app/store/selector/initialize.selector'
import { CommonService } from 'src/app/services/common/common.service'

@Component({
  selector: 'app-activity-pending-items',
  templateUrl: './activity-pending-items.component.html',
  styleUrls: ['./activity-pending-items.component.scss']
})
export class ActivityPendingItemsComponent implements OnDestroy {
  readonly DASHBOARD_LINK = `/${GlobalConstants.ROUTING.DASHBOARD}`
  summaryOptions: SummaryDataTableOptions
  summaryData: SummaryDataTableData<ActivityAPIModel>
  pendingData: ActivityAPIModel[]
  activitySummaryAPIData$: Observable<PaginatedAPI<ActivityAPIModel>>
  activityTypeData$: Observable<activityTypes>

  private uiStatusMapping: Map<string, string>
  status: string
  comment: string
  fromDate: string
  toDate: string
  activityStatus = GlobalConstants.ACTIVITY_STATUS_LIST
  isPendingActivitesUpdated$: Subscription
  user$: Subscription
  user: UserAPIModel
  readonly userType = GlobalConstants.USER_TYPE

  constructor(
    private appState: Store<AppState>,
    private documentService: DocumentService,
    private activity: ActivityService,
    private cs: CommonService
  ) {}

  queryParamValues = {
    pageNo: 0,
    pageLimit: SummaryDataTableComponent.defaultLimitDropdownValue,
    mapReferenceDocuments: false,
    sortBy: 'createdDatetime',
    sortOrder: 'ASC'
  }

  searchRequestBody: any = {
    responseOverdue: true,
    fromDate: '',
    toDate: ''
  }

  ngOnInit(): void {
    this.appState.select(getUIStatusMappingFromState).subscribe(uiStatusMapping => {
      if (uiStatusMapping) {
        this.uiStatusMapping = uiStatusMapping
      }
    })
    this.getUserDetailsFromStore()
    this.appState.subscribe(ee => update_activity_pending_item_success)
    this.activitySummaryAPIData$ = this.appState.select(activitySummaryDetailsSelector())
    this.appState.dispatch(fetch_activity_types_data({}))
    this.isPendingActivitesUpdated$ = this.appState
      .select(isPendingActivitesUpdated())
      .subscribe(isPendingActivitesUpdated => {
        if (isPendingActivitesUpdated) {
          this.comment = ''
          this.status = ''
          this.summaryData.checkBoxSelectionIds = []
          this.loadSummaryData()
        }
      })

    this.getPendingItems()
    this.summaryOptions = {
      downloadable: true,
      additionalButtons: false,
      contentSelectable: true,
      contentSelector: false,
      filterable: true,
      formattedContent: true,
      pageable: false,
      sortable: true,
      quickFilters: true
    }

    this.summaryData = {
      content$: this.activitySummaryAPIData$,
      contentSelector: {
        label: 'View for Days',
        selectedValue: this.queryParamValues.pageLimit.toString(),
        selectionOptions: [],
        onChange: this.onViewForChange
      },
      formattingLegend: [
        {
          formatLabel: 'High Priority',
          formatClassName: 'priority-high'
        },
        {
          formatLabel: 'Medium Priority',
          formatClassName: 'priority-medium'
        },
        {
          formatLabel: 'Low Priority',
          formatClassName: 'priority-low'
        }
      ],
      headers: [
        COMPANY_ENTITY_DISPLAY_ID,
        PENDING_ACTIVITY_ID,
        ACTIVITY_NAME,
        REQUEST_INITIATED_ON,
        DAYS_ELAPSED,
        RESPONSE_DUE_BY,
        TASK_CURRENT_STATUS,
        CREATED,
        ASSIGNED_TO_DISPLAY_ID
      ],
      additionalButtons: [],
      sortCallback: this.sortCallback,
      rowFormatting: this.rowFormattingForDaysElapsed,
      currentSearchParams: this.queryParamValues,
      searchCallback: this.searchCallback,
      downloadDocumentCallback: this.downloadDocumentCallback,
      checkBoxSelectionIds: [],
      selectPageCallback: this.selectPageCallback,
      onDropdownLimitChange: this.onDropdownLimitChange
    }
  }

  typeChange(typeCode: any) {
    this.activityTypeData$.subscribe((typeRes: any) => {
      const activitytype = typeRes
      const activityStatus = activitytype.filter(res => {
        return res.code == typeCode
      })
      this.searchRequestBody = {
        ...this.searchRequestBody,
        taskCode: activityStatus[0]?.code
      }
      this.loadSummaryData()
    })
  }

  dateSelection() {
    if (this.fromDate && this.toDate) {
      this.searchRequestBody = {
        ...this.searchRequestBody,
        responseOverdue: true,
        fromDate: this.fromDate,
        toDate: this.toDate
      }
      this.loadSummaryData()
    }
  }
  private loadSummaryData() {
    this.addNewJoineeWorkflowFilter(this.user?.userType)
    const options = {
      queryParams: this.buildQueryParams(),
      searchRequestBody: this.searchRequestBody
    }
    this.appState.dispatch(LOAD_ACTIVITY_SUMMARY_DETAILS(options))
  }

  sortCallback = (columnCode, order) => {
    this.queryParamValues.sortBy = columnCode
    this.queryParamValues.sortOrder = order
    this.loadSummaryData()
  }

  searchCallback = searchParams => {
    let selectedStatus = []
    if (searchParams.statusList) {
      let selected = searchParams.statusList
      for (let [key, value] of Object.entries(this.uiStatusMapping)) {
        if (selected === value) {
          selectedStatus.push(key)
        }
      }
    }
    this.queryParamValues.pageNo = 0
    this.searchRequestBody = {
      ...searchParams,
      statusList: selectedStatus
    }
    this.loadSummaryData()
  }

  onViewForChange = $event => {
    if ($event.target.value != 'undefined') {
      this.queryParamValues.pageLimit = $event.target.value
    } else {
      this.queryParamValues.pageLimit = undefined
    }
    this.loadSummaryData()
  }

  downloadDocumentCallback = documentType => {
    const checkBoxSelectionIds = this.summaryData.checkBoxSelectionIds
    if (checkBoxSelectionIds.length > 0) {
      this.searchRequestBody = {
        ids: checkBoxSelectionIds
      }
    }
    const endPoint = `${ServiceUrlConstants.API_ACTIVITY_SUMMARY_LIST + ServiceUrlConstants.API_EXPORT + documentType}`

    let options = {
      queryParams: this.buildQueryParams(true),
      requestBody: this.searchRequestBody
    }

    this.documentService.downloadDocument(endPoint, options)
  }

  rowFormattingForDaysElapsed = (record: ActivityAPIModel): string => {
    if (record.daysElapsed < 10) {
      return 'priority-low'
    } else if (record.daysElapsed < 20) {
      return 'priority-medium'
    } else {
      return 'priority-high'
    }
  }

  buildQueryParams = (buildForDownload = false): { key: string; value: string }[] => {
    return [
      {
        key: 'pageNo',
        value: buildForDownload
          ? GlobalConstants.FILE_DOWNLOAD_CONFIG.PAGE_NUMBER.toString()
          : this.queryParamValues.pageNo.toString()
      },
      {
        key: 'limit',
        value: buildForDownload
          ? GlobalConstants.FILE_DOWNLOAD_CONFIG.ROW_LIMIT.toString()
          : this.queryParamValues.pageLimit.toString()
      },
      {
        key: 'mapReferenceDocuments',
        value: 'false'
      },
      {
        key: 'sortBy',
        value: this.queryParamValues.sortBy
      },
      {
        key: 'sortOrder',
        value: this.queryParamValues.sortOrder
      }
    ]
  }

  downloadSelected = () => {}

  selectPageCallback = pageNum => {
    this.queryParamValues.pageNo = pageNum
    this.loadSummaryData()
  }

  getPendingItems() {
    this.activitySummaryAPIData$.subscribe((res: any) => {
      this.pendingData = res?.content
    })
  }

  save() {
    const selectedIds = this.summaryData.checkBoxSelectionIds
    if (!selectedIds || selectedIds.length === 0) {
      return this.cs.showError('Please select atleast one item')
    }

    if (this.status && !this.comment) {
      return this.cs.showError('Add comment')
    }

    if (!this.comment) {
      return this.cs.showError('Add Comment')
    }
    const filteredObject = this.pendingData
      .filter((item: any) => selectedIds?.includes(item.id))
      .reduce((result, item) => {
        result[item.id.toString()] = item.companyEntityId.toString()
        return result
      }, {})

    const PendingPayload = {
      elementIdCompanyIdMap: filteredObject,
      status: this.status,
      comment: this.comment
    }
    this.activity.dispatchBulkUpdate(PendingPayload)
  }

  private onDropdownLimitChange = (limit: number) => {
    this.queryParamValues.pageLimit = limit
    this.loadSummaryData()
  }

  getUserDetailsFromStore = () => {
    this.user$ = this.appState.select(getUserFromState).subscribe((user: UserAPIModel) => {
      if (user) {
        this.user = user
        if (user?.userType === this.userType.RMS_ADMIN) {
          this.addNewJoineeWorkflowFilter(user?.userType)
        } else {
          this.activityTypeData$ = this.appState.select(getActivityTypes())
        }
        this.loadSummaryData()
      }
    })
  }

  private addNewJoineeWorkflowFilter(userType: string) {
    if (userType != this.userType.RMS_ADMIN) {
      return
    }
    const activityTypes: any = [
      {
        code: 'EPF_NEW_JOINEE_WORKFLOW',
        label: 'New Joinee Workflow'
      }
    ]
    this.activityTypeData$ = of(activityTypes)
    this.searchRequestBody = { ...this.searchRequestBody, taskCode: activityTypes[0].code }
  }

  ngOnDestroy(): void {
    this.isPendingActivitesUpdated$?.unsubscribe()
  }
}
